/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef, useState } from 'react';

import { Button, Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';
import { CloseIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/xdp';

type Props = {
  message?: React.ReactNode;
};

const styles = {
  container: (height: string) => css`
    background: var(--cds-color-blue-100);
    transition: all 0.3s ease-in-out;
    overflow: hidden;
    height: ${height};
  `,
  hideBanner: css`
    height: 0;
    visibility: hidden;
  `,
  content: css`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--cds-spacing-100) 0;

    ${breakpoints.down('xs')} {
      justify-content: left;
    }
  `,
  message: css`
    margin-right: var(--cds-spacing-400);
  `,
};

const PageBannerModule = ({ message }: Props) => {
  const [hideBanner, setBannerState] = useState<boolean>(false);
  const [height, setHeight] = useState<string>('auto');
  const containerRef = useRef<HTMLDivElement>(null);

  if (!message) {
    return null;
  }

  const onDismiss = () => {
    if (containerRef.current) {
      setHeight(`${containerRef.current.clientHeight}px`);
    }

    // We need to set the height to the current height so that we can animate the height when hiding the banner
    setTimeout(() => {
      setBannerState(true);
    }, 0);
  };

  return (
    <div css={[styles.container(height), hideBanner ? styles.hideBanner : []]} ref={containerRef}>
      <PageGridContainer>
        <Grid item xs={12}>
          <div css={styles.content}>
            <div css={styles.message}>{message}</div>
            <Button variant="ghost" size="small" icon={<CloseIcon />} iconPosition="after" onClick={onDismiss}>
              {_t('Dismiss')}
            </Button>
          </div>
        </Grid>
      </PageGridContainer>
    </div>
  );
};

export default PageBannerModule;
